import React from 'react';

/**
 * Airbnb-style minimal line icons for the "Ausstattung" (amenities) multi-enum.
 *
 * Icons are resolved by matching keywords against the option key and label, so
 * they keep working when amenities come from Hosted Assets (Sharetribe Console)
 * with German or English labels. Unknown amenities fall back to a clean check.
 */

// 24x24 stroke icons. Each entry is the inner SVG content (paths/circles).
const ICONS = {
  wifi: (
    <>
      <path d="M2 8.5a16 16 0 0 1 20 0" />
      <path d="M5 12a11 11 0 0 1 14 0" />
      <path d="M8.5 15.5a6 6 0 0 1 7 0" />
      <path d="M12 19h.01" />
    </>
  ),
  printer: (
    <>
      <path d="M6 9V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v5" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-2" />
      <rect x="6" y="14" width="12" height="8" rx="1" />
    </>
  ),
  meeting: (
    <>
      <path d="M2 4h20" />
      <path d="M3 4v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V4" />
      <path d="M12 16v4" />
      <path d="M9 20h6" />
    </>
  ),
  phone: (
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  desk: (
    <>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8" />
      <path d="M12 17v4" />
    </>
  ),
  kitchen: (
    <>
      <path d="M3 2v7c0 1.1.9 2 2 2h2a2 2 0 0 0 2-2V2" />
      <path d="M7 2v20" />
      <path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3zm0 0v7" />
    </>
  ),
  coffee: (
    <>
      <path d="M17 8h1a4 4 0 0 1 0 8h-1" />
      <path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4z" />
      <path d="M6 2v2M10 2v2M14 2v2" />
    </>
  ),
  users: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  accessibility: (
    <>
      <circle cx="16" cy="4" r="1" />
      <path d="m18 19 1-7-6 1" />
      <path d="m5 8 3-3 5.5 3-2.36 3.5" />
      <path d="M4.24 14.5a5 5 0 0 0 6.88 6" />
      <path d="M13.76 17.5a5 5 0 0 0-6.88-6" />
    </>
  ),
  bike: (
    <>
      <circle cx="18.5" cy="17.5" r="3.5" />
      <circle cx="5.5" cy="17.5" r="3.5" />
      <circle cx="15" cy="5" r="1" />
      <path d="M12 17.5V14l-3-3 4-3 2 3h2" />
    </>
  ),
  paw: (
    <>
      <circle cx="11" cy="4" r="2" />
      <circle cx="18" cy="8" r="2" />
      <circle cx="20" cy="16" r="2" />
      <path d="M9 10a5 5 0 0 1 5 5v3.5a3.5 3.5 0 0 1-6.84 1.045Q6.52 17.48 4.46 16.84A3.5 3.5 0 0 1 5.5 10z" />
    </>
  ),
  air: (
    <>
      <path d="M17.7 7.7a2.5 2.5 0 1 1 1.8 4.3H2" />
      <path d="M9.6 4.6A2 2 0 1 1 11 8H2" />
      <path d="M12.6 19.4A2 2 0 1 0 14 16H2" />
    </>
  ),
  leaf: (
    <>
      <path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8 0 5.5-4.78 10-10 10z" />
      <path d="M2 21c0-3 1.85-5.36 5.08-6C9.5 14.52 12 13 13 12" />
    </>
  ),
  parking: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M9 17V7h4a3 3 0 0 1 0 6H9" />
    </>
  ),
  lock: (
    <>
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </>
  ),
  shower: (
    <path d="M12 2.69 17 8a7 7 0 1 1-10 0z" />
  ),
  water: (
    <>
      <path d="M6 2h12l-1 8H7z" />
      <path d="M7 10v8a3 3 0 0 0 3 3h4a3 3 0 0 0 3-3v-8" />
    </>
  ),
  outdoor: (
    <>
      <circle cx="12" cy="8" r="5" />
      <path d="M12 13v9" />
      <path d="M9 19h6" />
    </>
  ),
  lift: (
    <>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="m9 9 1.5-2 1.5 2M15 15l-1.5 2-1.5-2" />
    </>
  ),
  screen: (
    <>
      <rect x="2" y="3" width="20" height="13" rx="2" />
      <path d="m7 21 5-5 5 5" />
    </>
  ),
  board: (
    <>
      <rect x="3" y="4" width="18" height="13" rx="2" />
      <path d="M12 17v4" />
      <path d="M7 9h6M7 12h4" />
    </>
  ),
  check: (
    <path d="M20 6 9 17l-5-5" />
  ),
};

// Ordered keyword matchers. First match wins, so specific rules go before
// generic ones (e.g. accessibility before plain "Zugang"/clock).
const MATCHERS = [
  [/(wlan|wi[\s-]?fi|wifi|wireless|internet)/, 'wifi'],
  [/(druck|print|scan|kopier|copier|copy)/, 'printer'],
  [/(meeting|sitzung|konferenz|conference|besprechung|seminar)/, 'meeting'],
  [/(telefon|phone|booth|kabine|call.?box)/, 'phone'],
  [/(barrierefrei|rollstuhl|wheelchair|accessib|behinderten)/, 'accessibility'],
  [/(24\s*[/_-]?\s*7|rund um die uhr|jederzeit|24h)/, 'clock'],
  [/(ergonom|steh|schreibtisch|desk|workstation|arbeitsplatz|workspace)/, 'desk'],
  [/(kaffee|coffee|\btee\b|\btea\b|barista|espresso)/, 'coffee'],
  [/(kueche|küche|kitchen|aufenthalt|lounge|pantry|teekueche|teekueche)/, 'kitchen'],
  [/(community|event|network|networking|veranstalt)/, 'users'],
  [/(fahrrad|\bvelo|\bbike\b|bicycle|\bcycling\b)/, 'bike'],
  [/(haustier|\bhund|\bdog|\bpet|\bpaw|tierfreundlich)/, 'paw'],
  [/(klima|bel(ü|ue)ftung|l(ü|ue)ftung|air.?con|aircon|ventilation|\ba\/?c\b)/, 'air'],
  [/(nachhalt|(ö|oe)ko|\beco\b|recycl|sustain|green|umwelt|solar)/, 'leaf'],
  [/(park|stellplatz|garage|tiefgarage)/, 'parking'],
  [/(locker|schliessfach|schließfach|spind|\bsafe\b|abschliess)/, 'lock'],
  [/(dusche|shower)/, 'shower'],
  [/(wasser|water|getr(ä|ae)nk|drinks|erfrisch)/, 'water'],
  [/(garten|terrasse|terrace|garden|balkon|balcony|outdoor|aussenbereich|außenbereich|dachterrasse)/, 'outdoor'],
  [/(lift|aufzug|elevator|fahrstuhl)/, 'lift'],
  [/(beamer|projector|screen|bildschirm|monitor|\btv\b|leinwand)/, 'screen'],
  [/(whiteboard|flipchart|tafel|pinnwand)/, 'board'],
];

export const resolveIconName = (optionKey = '', label = '') => {
  const haystack = `${optionKey} ${label}`.toLowerCase();
  const match = MATCHERS.find(([re]) => re.test(haystack));
  return match ? match[1] : 'check';
};

/**
 * @param {Object} props
 * @param {string} [props.optionKey] - The amenity option key
 * @param {string} [props.label] - The amenity label (used for keyword matching)
 * @param {string} [props.className] - Optional class for the <svg>
 */
const AmenityIcon = props => {
  const { optionKey, label, className } = props;
  const name = resolveIconName(optionKey, label);
  return (
    <svg
      className={className}
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      role="none"
      aria-hidden="true"
    >
      {ICONS[name]}
    </svg>
  );
};

export default AmenityIcon;
